/*
 * Alarm state store, signal-backed. Concept C.6:
 *  - server pushes every lifecycle transition (raised / cleared / acknowledged)
 *    over the SignalR `alarms` channel; the hub auto-joins `alarms-{userId}`
 *    on connect so we do not need an explicit subscribe call.
 *  - on (re)connect the client pulls `GET /api/alarms/active` to seed the
 *    snapshot — the hub does not replay missed events.
 *  - acknowledgement is a writing action (`POST /api/alarms/{id}/acknowledge`);
 *    the SignalR broadcast is the source of truth for the resulting row state,
 *    so we do NOT optimistically mutate on success.
 *
 * Kept module-scope (not per-component) because the alarm list is process-
 * global and shared between the top-bar indicator, `/alarms`, and the leaf
 * warn icons on `/plants/:id/schema`.
 */

import { signal, computed, type Signal, type ReadonlySignal } from '@preact/signals';
import { api } from '../api/client';
import type { AlarmDto, AlarmEvent, ListActiveAlarmsResponse } from '../api/types';
import { ensureHubStarted, onAlarms, onReconnected } from './hubClient';

/** Live alarm list. Deduplicated by `id`; latest state wins. Includes
 *  Acknowledged rows because operators still want to see them in the top-bar
 *  and on `/alarms` until they clear. Excludes Cleared rows once we observe
 *  them (server won't re-push them, and the snapshot API only returns Active).
 *
 *  Note on ordering: we keep insertion-order for now — pages sort by
 *  `triggeredAt` themselves so the store doesn't need to. */
export const activeAlarmsSignal = signal<AlarmDto[]>([]);

/** Derived: how many alarms are currently Active (not yet Acknowledged, not
 *  Cleared). Feeds the top-bar indicator directly. */
export const activeCountSignal: ReadonlySignal<number> = computed(() =>
  activeAlarmsSignal.value.reduce((n, a) => (a.state === 'Active' ? n + 1 : n), 0),
);

/** Derived: any active alarm has severity `Alarm` (not just `Warning`).
 *  Used by the indicator to pick error tone vs. warn tone. */
export const hasActiveAlarmSeveritySignal: ReadonlySignal<boolean> = computed(() =>
  activeAlarmsSignal.value.some((a) => a.state === 'Active' && a.severity === 'Alarm'),
);

let bootstrapPromise: Promise<void> | null = null;

/** Fetch the active-alarms snapshot from the server and seed the signal.
 *  Idempotent — later calls return the same promise. Called once from
 *  `App.tsx` alongside `bootstrapSession()`. */
export function bootstrapAlarms(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;

  bootstrapPromise = (async () => {
    // Start the hub eagerly so the auto-join into `alarms-{userId}` happens
    // even before the user navigates into a plant. Without this, pushed events
    // arrive only after the first `subscribePlant()` — which is fine for the
    // schema page but not for the global indicator.
    try {
      await ensureHubStarted();
    } catch (err) {
      // Non-fatal — the snapshot pull below still gives us a first paint.
      // The hub reconnect loop will retry.
      // eslint-disable-next-line no-console
      console.warn('[alarmsStore] hub start failed, snapshot only', err);
    }

    await refreshAlarmSnapshot();
  })();

  return bootstrapPromise;
}

/** Pull the active-alarms snapshot fresh. Called on boot and after every
 *  hub reconnect — the server does not replay events during an outage,
 *  so without a re-snapshot the store would silently miss the world's
 *  changes. */
async function refreshAlarmSnapshot(): Promise<void> {
  try {
    const snap = await api.get<ListActiveAlarmsResponse>('/api/alarms/active');
    // Snapshot replaces whatever we may have gathered while the tab was
    // starting up (concept C.6: reconnect = snapshot pull). If a push
    // raced in first, prefer the server's authoritative view.
    const byId = new Map<string, AlarmDto>();
    for (const a of snap.alarms) byId.set(a.id, a);
    // Fold any locally-observed rows that the snapshot might not know
    // about yet (unlikely; but harmless dedupe).
    for (const a of activeAlarmsSignal.value) if (!byId.has(a.id)) byId.set(a.id, a);
    activeAlarmsSignal.value = Array.from(byId.values());
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[alarmsStore] snapshot fetch failed', err);
  }
}

// On every hub reconnect (auto-recovered drop, or our safety-net retry),
// re-pull the active snapshot to catch anything we missed during the outage.
onReconnected(() => { void refreshAlarmSnapshot(); });

/** Apply an event to the signal. Extracted so tests could drive it directly.
 *
 *  Defensive on the wire shape: the SignalR broadcast has occasionally come
 *  in without a populated <c>a</c> field (server bug or misordered
 *  cleared-broadcast during app boot). Without the guard the handler
 *  crashes at <c>evt.a.id</c>, the hubClient try/catch swallows the throw,
 *  and the store silently stops applying updates — the UI "freezes" from
 *  the operator's perspective. Better to skip a malformed event with a
 *  console.warn than to freeze the whole alarm view.
 */
function applyEvent(evt: AlarmEvent): void {
  if (!evt || !evt.a || typeof evt.a.id !== 'string') {
    // eslint-disable-next-line no-console
    console.warn('[alarmsStore] dropped malformed alarm event', evt);
    return;
  }
  const list = activeAlarmsSignal.value;
  const idx = list.findIndex((a) => a.id === evt.a.id);

  if (evt.t === 'Cleared') {
    // MVP behaviour: keep the row in the list with state = Cleared so
    // operators still see what just resolved. Full-cleared rows aren't
    // interesting to the top-bar count (activeCountSignal already ignores
    // non-Active states) but the /alarms page can filter them out itself.
    if (idx === -1) {
      activeAlarmsSignal.value = [...list, evt.a];
    } else {
      const next = list.slice();
      next[idx] = evt.a;
      activeAlarmsSignal.value = next;
    }
    return;
  }

  if (idx === -1) {
    activeAlarmsSignal.value = [...list, evt.a];
  } else {
    const next = list.slice();
    next[idx] = evt.a;
    activeAlarmsSignal.value = next;
  }
}

// Wire the hub listener once, on first import. The handler survives hub
// reconnects (SignalR just re-invokes `.on('alarms', ...)` on the same
// registration), so a single subscription is enough for the app lifetime.
onAlarms(applyEvent);

/** Look up the currently-active alarm for a given leaf id, if any.
 *  Non-reactive — callers reading this inside a `.value` block on
 *  `activeAlarmsSignal` will already re-render on updates.
 *
 *  Returns the highest-severity active row (Alarm > Warning). If multiple
 *  alarms exist for the same leaf we prefer the most recent trigger. */
export function findActiveAlarmForLeaf(leafId: string, list: AlarmDto[]): AlarmDto | null {
  let best: AlarmDto | null = null;
  for (const a of list) {
    if (a.leafId !== leafId) continue;
    if (a.state !== 'Active' && a.state !== 'Acknowledged') continue;
    if (!best) { best = a; continue; }
    // Prefer higher severity, then newer triggeredAt.
    const bestRank = best.severity === 'Alarm' ? 1 : 0;
    const curRank = a.severity === 'Alarm' ? 1 : 0;
    if (curRank > bestRank) { best = a; continue; }
    if (curRank === bestRank && a.triggeredAt > best.triggeredAt) best = a;
  }
  return best;
}

/** Reactive signal wrapper around `findActiveAlarmForLeaf`, provided for
 *  callers that prefer signal-composition over reading inside a render body.
 *  Prefer inlining the filter inside the LeafRow — it's cheaper because we
 *  avoid one computed subscription per leaf (thousands of leaves × one
 *  computed = wasted subscriptions). Kept here for completeness. */
export function leafAlarmSignal(leafId: string): ReadonlySignal<AlarmDto | null> {
  return computed(() => findActiveAlarmForLeaf(leafId, activeAlarmsSignal.value));
}

/** Test/debug reset. Not part of the public API. */
export function __resetAlarmsStoreForTest(): void {
  activeAlarmsSignal.value = [];
  bootstrapPromise = null;
}

// Ensure the module-level Signal import survives tree-shaking scrutiny when
// this file is imported only for its side effects.
export type { Signal };
